import React, { useEffect, useState } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Users, TrendingUp, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import { getMyChildren, type ParentChild, getParentChildDashboard } from '../../modules/roster/api/roster.client';
import { Spinner } from '../../components/ui/Spinner';
import ParentTutorAnalyticsPanel from '../../components/parent/ParentTutorAnalyticsPanel';

export const ParentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [childrenError, setChildrenError] = useState<string | null>(null);

  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<any | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchChildren = async () => {
      setChildrenLoading(true);
      setChildrenError(null);
      try {
        const data = await getMyChildren();
        if (cancelled) return;
        setChildren(data);
        if (data.length > 0) {
          setSelectedChildId((prev) => prev ?? data[0].id);
        }
      } catch (err: any) {
        if (cancelled) return;
        const message = err?.response?.data?.message || err?.message || 'Failed to load children';
        setChildrenError(String(message));
      } finally {
        if (!cancelled) setChildrenLoading(false);
      }
    };
    fetchChildren();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedChildId) {
      setDashboard(null);
      return;
    }
    let cancelled = false;
    const fetchDashboard = async () => {
      setDashboardLoading(true);
      setDashboardError(null);
      try {
        const res = await getParentChildDashboard(selectedChildId);
        if (cancelled) return;
        setDashboard(res);
      } catch (err: any) {
        if (cancelled) return;
        const message = err?.response?.data?.message || err?.message || 'Failed to load dashboard';
        setDashboardError(String(message));
        setDashboard(null);
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };
    fetchDashboard();
    return () => { cancelled = true; };
  }, [selectedChildId]);

  const retryChildren = () => {
    setChildren([]);
    setChildrenLoading(true);
    setChildrenError(null);
    setSelectedChildId(null);
    // trigger fetch via effect by setting state (simple approach)
    getMyChildren().then((data) => {
      setChildren(data);
      if (data.length > 0) setSelectedChildId(data[0].id);
    }).catch((e) => setChildrenError(String(e?.message || 'Failed to load children'))).finally(() => setChildrenLoading(false));
  };

  const retryDashboard = () => {
    if (selectedChildId) {
      setDashboardError(null);
      setDashboardLoading(true);
      getParentChildDashboard(selectedChildId).then((res) => setDashboard(res)).catch((e) => setDashboardError(String(e?.message || 'Failed to load dashboard'))).finally(() => setDashboardLoading(false));
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Parent Dashboard"
        subtitle="Monitor your children's progress"
        actions={
          <Button onClick={() => navigate(ROUTES.PARENT_LINK_CHILD)}>
            Link Child
          </Button>
        }
      />

      {childrenLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : childrenError ? (
        <Card>
          <CardContent padding="lg" className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{childrenError}</p>
            <Button variant="outline" onClick={retryChildren}>Retry</Button>
          </CardContent>
        </Card>
      ) : children.length === 0 ? (
        <Card>
          <CardContent padding="lg" className="text-center">
            <h3 className="text-lg font-semibold mb-2">No Children Linked</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Link your account to your child's student record to view their details.
            </p>
            <Button onClick={() => navigate(ROUTES.PARENT_LINK_CHILD)}>
              Link Child
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Child selector */}
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {children.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedChildId(c.id)}
                  className={`px-4 py-2 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    selectedChildId === c.id
                      ? 'bg-primary text-white border-transparent hover:bg-primary/90 dark:bg-primary-600'
                      : 'bg-white text-slate-900 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700'
                  }`}
                >
                  {c.display_name}
                </button>
              ))}
            </div>
          </div>

          {/* Cards: child overview, teachers, learning — full-width row that becomes 3 columns on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-lg">
                    <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <CardTitle>Child Overview</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <h3 className="text-lg font-semibold">{children.find(ch => ch.id === selectedChildId)?.display_name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {(() => {
                    // derive grade: 1) dashboard.child.grade 2) selected child.class?.grade 3) parse dashboard.child.class_name 4) fallback
                    const dashChild = dashboard?.child;
                    const selected = children.find((ch) => ch.id === selectedChildId);
                    const gradeFromDashboard = dashChild?.grade;
                    const gradeFromClass = selected?.class?.grade;
                    let parsedFromClassName: string | null = null;
                    const className = dashChild?.class_name;
                    if (!gradeFromDashboard && className && typeof className === 'string') {
                      const match = className.match(/Grade\s*(\d+)/i);
                      if (match) parsedFromClassName = match[1];
                    }
                    const finalGrade = gradeFromDashboard ?? gradeFromClass ?? parsedFromClassName ?? null;
                    return `Grade ${finalGrade ?? '—'}`;
                  })()}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-success-100 dark:bg-success-900 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-success-600 dark:text-success-400" />
                  </div>
                  <CardTitle>Linked Teachers</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {dashboardLoading ? (
                  <div className="py-4"><Spinner /></div>
                ) : dashboardError ? (
                  <div className="text-sm text-red-600">{dashboardError}</div>
                  ) : !dashboard || !dashboard.teachers || dashboard.teachers.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">No teachers linked</p>
                ) : (
                  <ul className="space-y-2">
                    {dashboard.teachers.map((t: any, idx: number) => (
                      <li key={t.id ?? t.platform_user_id ?? idx} className="text-sm">
                        <div className="font-medium">{t.display_name ?? t.first_name ?? '—'}</div>
                        {t.email && <div className="text-xs text-gray-500">{t.email}</div>}
                        {t.subject && <div className="text-xs text-gray-500">Subject: {t.subject}</div>}
                        {t.class_name && <div className="text-xs text-gray-500">Class: {t.class_name}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-secondary-100 dark:bg-secondary-900 rounded-lg">
                    <Calendar className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
                  </div>
                  <CardTitle>Learning Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {dashboardLoading ? (
                  <div className="py-4"><Spinner /></div>
                ) : dashboard?.learning ? (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{dashboard.learning.summary ?? 'Learning data available'}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">No learning summary available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tutor analytics section (unchanged) */}
          <div>
            <ParentTutorAnalyticsPanel
              data={dashboard ? dashboard.tutor : null}
              loading={dashboardLoading}
              error={dashboardError}
              onRetry={retryDashboard}
              days={30}
            />
          </div>
        </>
      )}
    </Layout>
  );
};

